import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import FieldTypeahead from './shared/FieldTypeahead';

const ConditionalRuleForm = ({ configuration, onChange, errors, warnings, availableFields }) => {
  const operators = [
    { value: 'EXISTS', label: 'EXISTS - Field exists', hasValue: false },
    { value: 'NOT_EXISTS', label: 'NOT_EXISTS - Field does not exist', hasValue: false },
    { value: 'EQUALS', label: 'EQUALS - Field equals value', hasValue: true },
    { value: 'NOT_EQUALS', label: 'NOT_EQUALS - Field does not equal value', hasValue: true },
    { value: 'CONTAINS', label: 'CONTAINS - Field contains value', hasValue: true },
    { value: 'STARTS_WITH', label: 'STARTS_WITH - Field starts with value', hasValue: true },
    { value: 'ENDS_WITH', label: 'ENDS_WITH - Field ends with value', hasValue: true },
    { value: 'GREATER_THAN', label: 'GREATER_THAN - Field > value', hasValue: true },
    { value: 'LESS_THAN', label: 'LESS_THAN - Field < value', hasValue: true },
    { value: 'REGEX_MATCH', label: 'REGEX_MATCH - Field matches pattern', hasValue: true },
    { value: 'IS_EMPTY', label: 'IS_EMPTY - Field is empty/missing', hasValue: false },
    { value: 'IS_NOT_EMPTY', label: 'IS_NOT_EMPTY - Field has value', hasValue: false },
    { value: 'IN', label: 'IN - Field in list', hasValue: true, isArray: true }
  ];

  const actionTypes = [
    { value: 'set-value', label: 'Set Value' },
    { value: 'copy-field', label: 'Copy Field' },
    { value: 'apply-template', label: 'Apply Template' }
  ];

  const handleChange = (field, value) => {
    const newConfig = { ...configuration };
    newConfig[field] = value;
    onChange(newConfig);
  };

  const handleBranchChange = (branchIndex, updates) => {
    const newConfig = { ...configuration };
    if (!newConfig.conditions) {
      newConfig.conditions = [];
    }
    const conditions = [...newConfig.conditions];
    conditions[branchIndex] = { ...conditions[branchIndex], ...updates };
    newConfig.conditions = conditions;
    onChange(newConfig);
  };

  const handleConditionInBranchChange = (branchIndex, condIndex, field, value) => {
    const newConfig = { ...configuration };
    if (!newConfig.conditions) {
      newConfig.conditions = [];
    }
    const conditions = [...newConfig.conditions];
    const branch = { ...conditions[branchIndex] };

    // Get the conditions array for this branch
    const branchConditions = getBranchConditions(branch);
    const updatedConditions = [...branchConditions];

    if (!updatedConditions[condIndex]) {
      updatedConditions[condIndex] = { field: '', operator: 'EXISTS', value: '' };
    }
    updatedConditions[condIndex] = { ...updatedConditions[condIndex], [field]: value };

    // Serialize back: single condition → if, multiple → ifGroup
    if (updatedConditions.length === 1) {
      branch.if = updatedConditions[0];
      delete branch.ifGroup;
    } else {
      branch.ifGroup = {
        logicalOperator: branch.ifGroup?.logicalOperator || 'AND',
        conditions: updatedConditions
      };
      delete branch.if;
    }

    conditions[branchIndex] = branch;
    newConfig.conditions = conditions;
    onChange(newConfig);
  };

  const addConditionToBranch = (branchIndex) => {
    const newConfig = { ...configuration };
    if (!newConfig.conditions) {
      newConfig.conditions = [];
    }
    const conditions = [...newConfig.conditions];
    const branch = { ...conditions[branchIndex] };

    const branchConditions = getBranchConditions(branch);
    const updatedConditions = [...branchConditions, { field: '', operator: 'EXISTS', value: '' }];

    // Multiple conditions → use ifGroup
    branch.ifGroup = {
      logicalOperator: branch.ifGroup?.logicalOperator || 'AND',
      conditions: updatedConditions
    };
    delete branch.if;

    conditions[branchIndex] = branch;
    newConfig.conditions = conditions;
    onChange(newConfig);
  };

  const removeConditionFromBranch = (branchIndex, condIndex) => {
    const newConfig = { ...configuration };
    const conditions = [...newConfig.conditions];
    const branch = { ...conditions[branchIndex] };

    const branchConditions = getBranchConditions(branch);
    const updatedConditions = branchConditions.filter((_, i) => i !== condIndex);

    if (updatedConditions.length === 0) {
      // Keep at least one empty condition
      branch.if = { field: '', operator: 'EXISTS', value: '' };
      delete branch.ifGroup;
    } else if (updatedConditions.length === 1) {
      branch.if = updatedConditions[0];
      delete branch.ifGroup;
    } else {
      branch.ifGroup = {
        logicalOperator: branch.ifGroup?.logicalOperator || 'AND',
        conditions: updatedConditions
      };
      delete branch.if;
    }

    conditions[branchIndex] = branch;
    newConfig.conditions = conditions;
    onChange(newConfig);
  };

  const handleLogicalOperatorChange = (branchIndex, logicalOperator) => {
    const newConfig = { ...configuration };
    const conditions = [...newConfig.conditions];
    const branch = { ...conditions[branchIndex] };

    if (branch.ifGroup) {
      branch.ifGroup = { ...branch.ifGroup, logicalOperator };
    }

    conditions[branchIndex] = branch;
    newConfig.conditions = conditions;
    onChange(newConfig);
  };

  const handleActionTypeChange = (branchIndex, actionType) => {
    const newConfig = { ...configuration };
    const conditions = [...newConfig.conditions];
    const branch = { ...conditions[branchIndex] };
    branch.actionType = actionType;

    // Clear irrelevant fields when switching action type
    if (actionType === 'copy-field') {
      delete branch.then;
    } else {
      delete branch.sourceField;
    }

    conditions[branchIndex] = branch;
    newConfig.conditions = conditions;
    onChange(newConfig);
  };

  const handleActionValueChange = (branchIndex, field, value) => {
    const newConfig = { ...configuration };
    const conditions = [...newConfig.conditions];
    const branch = { ...conditions[branchIndex] };

    if (field === 'sourceField') {
      branch.sourceField = value;
    } else {
      branch.then = { ...branch.then, value };
    }

    conditions[branchIndex] = branch;
    newConfig.conditions = conditions;
    onChange(newConfig);
  };

  const addBranch = () => {
    const newConfig = { ...configuration };
    if (!newConfig.conditions) {
      newConfig.conditions = [];
    }
    newConfig.conditions.push({
      if: { field: '', operator: 'EXISTS', value: '' },
      then: { value: '' },
      actionType: 'set-value'
    });
    onChange(newConfig);
  };

  const removeBranch = (index) => {
    const newConfig = { ...configuration };
    newConfig.conditions = newConfig.conditions.filter((_, i) => i !== index);
    onChange(newConfig);
  };

  const handleElseChange = (value) => {
    const newConfig = { ...configuration };
    newConfig.else = { ...newConfig.else, value };
    onChange(newConfig);
  };

  // Helper: get conditions array from a branch (handles both if and ifGroup)
  const getBranchConditions = (branch) => {
    if (branch.ifGroup && branch.ifGroup.conditions && branch.ifGroup.conditions.length > 0) {
      return branch.ifGroup.conditions;
    }
    if (branch.if) {
      return [branch.if];
    }
    return [{ field: '', operator: 'EXISTS', value: '' }];
  };

  const getOperator = (operatorValue) => {
    return operators.find(op => op.value === operatorValue);
  };

  const getEffectiveActionType = (branch) => {
    return branch.actionType || 'set-value';
  };

  const conditions = configuration.conditions || [];

  // Logic Flow preview helpers
  const renderConditionPreview = (cond) => {
    const op = cond.operator || 'EXISTS';
    const field = cond.field || 'field';
    const opDef = getOperator(op);
    if (opDef && !opDef.hasValue) {
      return `${field} ${op}`;
    }
    return `${field} ${op} '${cond.value || ''}'`;
  };

  const renderBranchConditionPreview = (branch) => {
    const branchConds = getBranchConditions(branch);
    if (branchConds.length === 1) {
      return renderConditionPreview(branchConds[0]);
    }
    const logicalOp = branch.ifGroup?.logicalOperator || 'AND';
    const condStrings = branchConds.map(renderConditionPreview);
    return `(${condStrings.join(` ${logicalOp} `)})`;
  };

  const renderActionPreview = (branch, targetField) => {
    const actionType = getEffectiveActionType(branch);
    const target = targetField || 'target';
    switch (actionType) {
      case 'copy-field':
        return `COPY ${branch.sourceField || 'source_field'} → ${target}`;
      case 'apply-template':
        return `SET ${target} = "${branch.then?.value || '{template}'}"`;
      case 'set-value':
      default:
        return `SET ${target} = "${branch.then?.value || 'value'}"`;
    }
  };

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="targetField" className="form-label">
          Target Field <span className="text-danger">*</span>
        </label>
        <FieldTypeahead
          id="targetField"
          value={configuration.targetField || ''}
          onChange={(val) => handleChange('targetField', val)}
          suggestions={availableFields || []}
          placeholder="e.g., vendor_id"
          error={errors?.targetField}
          warning={warnings?.targetField}
        />
        <small className="form-text text-muted">
          The field to store the result in
        </small>
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label mb-0">
            Branches <span className="text-danger">*</span>
          </label>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addBranch}
            aria-label="Add Branch"
          >
            <FontAwesomeIcon icon={faPlus} /> Add Branch
          </button>
        </div>

        {errors?.conditions && (
          <div className="alert alert-danger py-2 mb-2" role="alert">
            {errors.conditions}
          </div>
        )}
        {warnings?.conditions && (
          <div className="text-warning mb-2">
            {warnings.conditions}
          </div>
        )}

        {conditions.length === 0 && (
          <div className="alert alert-info">
            No branches defined. Click &quot;Add Branch&quot; to create your first conditional branch.
          </div>
        )}

        {conditions.map((branch, branchIndex) => {
          const branchConditions = getBranchConditions(branch);
          const hasMultipleConditions = branchConditions.length > 1;
          const logicalOperator = branch.ifGroup?.logicalOperator || 'AND';
          const effectiveActionType = getEffectiveActionType(branch);

          return (
            <div key={branchIndex} className="card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <strong>Branch {branchIndex + 1}</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeBranch(branchIndex)}
                  aria-label={`Remove Branch ${branchIndex + 1}`}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
              <div className="card-body">
                {/* Logical Operator Toggle */}
                {hasMultipleConditions && (
                  <div className="mb-3">
                    <label
                      htmlFor={`branch-${branchIndex}-logical-operator`}
                      className="form-label"
                    >
                      Match
                    </label>
                    <select
                      id={`branch-${branchIndex}-logical-operator`}
                      className="form-select"
                      value={logicalOperator}
                      onChange={(e) => handleLogicalOperatorChange(branchIndex, e.target.value)}
                      aria-label={`Logical operator for Branch ${branchIndex + 1}`}
                    >
                      <option value="AND">ALL conditions (AND)</option>
                      <option value="OR">ANY condition (OR)</option>
                    </select>
                  </div>
                )}

                {/* Conditions within the branch */}
                {branchConditions.map((cond, condIndex) => {
                  const selectedOperator = getOperator(cond.operator);

                  return (
                    <div key={condIndex} className="border rounded p-2 mb-2 bg-light">
                      <div className="row align-items-end">
                        <div className="col-md-3 mb-2">
                          <label
                            htmlFor={`branch-${branchIndex}-cond-${condIndex}-field`}
                            className="form-label small"
                          >
                            Field
                          </label>
                          <FieldTypeahead
                            id={`condition-field-${branchIndex}-${condIndex}`}
                            value={cond.field || ''}
                            onChange={(val) => handleConditionInBranchChange(branchIndex, condIndex, 'field', val)}
                            suggestions={availableFields || []}
                            placeholder="Field name"
                            className="form-control-sm"
                          />
                        </div>

                        <div className="col-md-3 mb-2">
                          <label
                            htmlFor={`branch-${branchIndex}-cond-${condIndex}-operator`}
                            className="form-label small"
                          >
                            Operator
                          </label>
                          <select
                            id={`branch-${branchIndex}-cond-${condIndex}-operator`}
                            className="form-select form-select-sm"
                            value={cond.operator || 'EXISTS'}
                            onChange={(e) => handleConditionInBranchChange(branchIndex, condIndex, 'operator', e.target.value)}
                          >
                            {operators.map(op => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {selectedOperator?.hasValue && (
                          <div className="col-md-4 mb-2">
                            <label
                              htmlFor={`branch-${branchIndex}-cond-${condIndex}-value`}
                              className="form-label small"
                            >
                              Value
                            </label>
                            <input
                              type="text"
                              id={`branch-${branchIndex}-cond-${condIndex}-value`}
                              className="form-control form-control-sm"
                              value={cond.value || ''}
                              onChange={(e) => handleConditionInBranchChange(branchIndex, condIndex, 'value', e.target.value)}
                              placeholder={selectedOperator.isArray ? 'value1, value2, value3' : 'Comparison value'}
                            />
                            {selectedOperator.isArray && (
                              <small className="form-text text-muted">
                                Comma-separated list of values
                              </small>
                            )}
                          </div>
                        )}

                        <div className="col-md-2 mb-2 d-flex align-items-end">
                          {branchConditions.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeConditionFromBranch(branchIndex, condIndex)}
                              aria-label={`Remove condition ${condIndex + 1} from Branch ${branchIndex + 1}`}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mb-3"
                  onClick={() => addConditionToBranch(branchIndex)}
                  aria-label={`Add Condition to Branch ${branchIndex + 1}`}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Condition to Branch
                </button>

                {/* Action Type Section */}
                <hr />
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label
                      htmlFor={`branch-${branchIndex}-action-type`}
                      className="form-label"
                    >
                      Then (Action Type)
                    </label>
                    <select
                      id={`branch-${branchIndex}-action-type`}
                      className="form-select"
                      value={effectiveActionType}
                      onChange={(e) => handleActionTypeChange(branchIndex, e.target.value)}
                    >
                      {actionTypes.map(at => (
                        <option key={at.value} value={at.value}>
                          {at.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-8 mb-3">
                    {effectiveActionType === 'set-value' && (
                      <>
                        <label
                          htmlFor={`branch-${branchIndex}-then-value`}
                          className="form-label"
                        >
                          Value
                        </label>
                        <input
                          type="text"
                          id={`branch-${branchIndex}-then-value`}
                          className="form-control"
                          value={branch.then?.value || ''}
                          onChange={(e) => handleActionValueChange(branchIndex, 'value', e.target.value)}
                          placeholder="Use {field_name} for field substitution"
                        />
                        <small className="form-text text-muted">
                          Use curly braces to reference field values, e.g., {'{vendor_id}'}
                        </small>
                      </>
                    )}

                    {effectiveActionType === 'copy-field' && (
                      <>
                        <label
                          htmlFor={`branch-${branchIndex}-source-field`}
                          className="form-label"
                        >
                          Source Field
                        </label>
                        <FieldTypeahead
                          id={`branch-${branchIndex}-source-field`}
                          value={branch.sourceField || ''}
                          onChange={(val) => handleActionValueChange(branchIndex, 'sourceField', val)}
                          suggestions={availableFields || []}
                          placeholder="Field to copy value from"
                        />
                        <small className="form-text text-muted">
                          The value of this field will be copied to the target field
                        </small>
                      </>
                    )}

                    {effectiveActionType === 'apply-template' && (
                      <>
                        <label
                          htmlFor={`branch-${branchIndex}-template-value`}
                          className="form-label"
                        >
                          Template
                        </label>
                        <input
                          type="text"
                          id={`branch-${branchIndex}-template-value`}
                          className="form-control"
                          value={branch.then?.value || ''}
                          onChange={(e) => handleActionValueChange(branchIndex, 'value', e.target.value)}
                          placeholder="{vendor_name} - {invoice_number}"
                        />
                        <small className="form-text text-muted">
                          Use {'{field}'} to reference multiple fields, e.g., {'{vendor_name} - {amount}'}
                        </small>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mb-3">
        <label htmlFor="elseValue" className="form-label">
          Else Value (Default)
        </label>
        <input
          type="text"
          id="elseValue"
          className="form-control"
          value={configuration.else?.value || ''}
          onChange={(e) => handleElseChange(e.target.value)}
          placeholder="Default value if no branches match"
        />
        <small className="form-text text-muted">
          This value will be used if none of the branches above match
        </small>
      </div>

      {/* Logic Flow Preview */}
      {conditions.length > 0 && (
        <div className="alert alert-secondary">
          <strong>Logic Flow:</strong>
          <div className="mt-2" style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
            {conditions.map((branch, index) => (
              <div key={index} className="mb-1">
                {index === 0 ? 'IF' : 'ELSE IF'} {renderBranchConditionPreview(branch)}
                <br />
                &nbsp;&nbsp;THEN {renderActionPreview(branch, configuration.targetField)}
              </div>
            ))}
            {configuration.else?.value && (
              <div>
                ELSE
                <br />
                &nbsp;&nbsp;THEN SET {configuration.targetField || 'target'} = &quot;{configuration.else.value}&quot;
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConditionalRuleForm;
